import OnboardingPageTitle from "./OnboardingPageTitle";
import i18n from "../../i18n";import { Trans } from "react-i18next";import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./shiftMapping.css";
import { resolveApiUrl } from "../../api/api";
import sessionDB from "../../utils/sessionDB";
import { fetchAllShifts } from "../../services/shiftService";

import {
  buildOnboardingAccessMessage,
  isOnboardingFeatureUnavailableError,
  isGenericOnboardingErrorMessage,
  parseOnboardingErrorResponse,
  handleOnboardingApiError,
  resolveOnboardingActionConfig } from
"./onboardingFeatureAccess";
import { format, parse } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger } from
"../../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"../../components/ui/select";

const DEFAULT_SHIFT_MAPPING_ENDPOINT = "/saas/hrms/emp-shift-mapping/add";
const DEFAULT_SHIFT_MAPPING_SERVICE_CODE = "HR25A";

function mapShiftsToOptions(shifts) {
  return (Array.isArray(shifts) ? shifts : [])
  .map((shift) => {
    const value = shift?.shiftId ?? shift?.id;
    if (value == null || value === "") return null;
    return {
      value: String(value),
      label: String(shift?.shiftName || `${i18n.t("onboarding.shift")} ${value}`).trim() || `${i18n.t("onboarding.shift")} ${value}`
    };
  })
  .filter(Boolean);
}

function resolveShiftIdFromRecord(record, options = []) {
  const idCandidate = record?.shiftId ?? record?.shift_id ?? record?.orgShiftId;
  if (idCandidate != null && String(idCandidate).trim() !== "") {
    return String(idCandidate);
  }

  const nameCandidate = String(record?.shiftName ?? record?.shiftCode ?? "").trim();
  if (!nameCandidate) return "";

  const match = options.find(
    (option) =>
    option.value === nameCandidate ||
    option.label.toLowerCase() === nameCandidate.toLowerCase()
  );
  // Keep the name temporarily so it can be resolved once options load.
  return match ? match.value : nameCandidate;
}

function isValidIsoDate(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || "").trim());
}

function normalizeToIsoDate(value) {
  if (!value) return "";
  let str = String(value).trim();
  if (str.includes("T")) str = str.split("T")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [day, month, year] = str.split("-");
    return `${year}-${month}-${day}`;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [day, month, year] = str.split("/");
    return `${year}-${month}-${day}`;
  }
  return "";
}

/* eslint-disable-next-line no-unused-vars -- retained for API error normalization */
function normalizeShiftMappingErrorMessage(message) {
  return String(message || "").trim() || i18n.t("onboarding.errors.shiftMappingRequestFailed");
}

export default function ShiftMapping({ accessToken, tenantCode, initialEmployeeId = "", breadcrumb, onBack, onNext, onSuccess, isTabMode = false, isActive = true, saveTriggerRef }) {
  console.log("ShiftMapping component rendered");
  useEffect(() => {
    if (saveTriggerRef) {
      saveTriggerRef.current = {
        submit: handleSave
      };
    }
  });
  const [form, setForm] = useState({
    empId: "",
    shiftId: "",
    effectiveFrom: "",
    effectiveTo: "",
    isActive: true
  });
  const [shiftOptions, setShiftOptions] = useState([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [banner, _setBanner] = useState(null);
  const setBanner = (val) => {
    _setBanner((prev) => {
      const next = typeof val === "function" ? val(prev) : val;
      if (next && next.type === "error") {
        if (isOnboardingFeatureUnavailableError(next.msg)) {
          return { type: "error", msg: i18n.t("onboarding.featureUnavailable") };
        }
        if (isGenericOnboardingErrorMessage(next.msg)) {
          return prev;
        }
      }
      return next;
    });
  };
  const [errors, setErrors] = useState({});
  const [isUpdate, setIsUpdate] = useState(false);
  // Stores the backend mapping record ID (mappingId) needed for the update endpoint
  const [mappingId, setMappingId] = useState(null);

  const sessionAccessToken = accessToken || sessionDB.getAccessToken();
  const tenantId = sessionDB.getTenantId();
  const shiftMappingAction = resolveOnboardingActionConfig({
    submoduleNames: ["Shift Mapping", "Employee Shifts Mapping"],
    action: isUpdate ? "update" : "add",
    fallback: {
      title: isUpdate ? "Update Shift Mapping" : "Add Shift Mapping",
      serviceCode: isUpdate ? "HR25U" : DEFAULT_SHIFT_MAPPING_SERVICE_CODE,
      endpoint: isUpdate ?
      `/saas/hrms/emp-shift-mapping/update/${mappingId}` :
      DEFAULT_SHIFT_MAPPING_ENDPOINT,
      method: isUpdate ? "PUT" : "POST"
    }
  });

  useEffect(() => {
    if (!isActive) return;
    let active = true;

    async function loadShifts() {
      console.log("Entered loadShifts");
      setShiftsLoading(true);
      try {
        /*const shifts = await fetchAllShifts({
          page: 0,
          size: 10,
          sortBy: "shiftName",
          sortDir: "ASC"
        });*/
        console.log("loadig shifts...");
        const shifts = await fetchAllShifts();
        console.log("Shift API Response:", shifts);
        if (!active) return;
        setShiftOptions(mapShiftsToOptions(shifts));

      } catch (error) {
        if (!active) return;
        console.error("Failed to load shifts", error);
        setShiftOptions([]);
        setBanner((current) => current || { type: "error", msg: error.message || i18n.t("onboarding.errors.unableToLoadShifts") });
      } finally {
        if (active) setShiftsLoading(false);
      }
    }

    loadShifts();
    return () => {
      active = false;
    };
  }, [isActive, tenantId]);

  const fetchShiftMapping = useCallback(async () => {
    if (!initialEmployeeId) return;
    try {
      const headers = {
        'x-tenant-code': tenantCode || sessionDB.getTenantCode() || "",
        'x-tenant-id': String(tenantId || sessionDB.getTenantId() || ""),
        'x-service-code': 'HR25S',
        authorization: `Bearer ${sessionAccessToken || sessionDB.getAccessToken() || ""}`,
        'Content-Type': 'application/json'
      };

      // CORRECT ENDPOINT: GET /saas/hrms/emp-shift-mapping/search/employee?empId={empId}
      // The old endpoint /search/{empId} does not exist (returns 400)
      const response = await fetch(
        resolveApiUrl(`/saas/hrms/emp-shift-mapping/search/employee?empId=${encodeURIComponent(Number(initialEmployeeId))}`),
        { method: 'GET', headers }
      );

      if (response.ok) {
        const body = await response.json();
        // Response is ApiResponsePagedResponseEmpShiftMappingDTO
        // Actual records are under body.data.content (paged) or body.data (list)
        const data = body.data || body;
        const candidates = [
        data?.content,
        data?.data?.content,
        data?.records,
        data?.items,
        data];

        const arrayCandidate = candidates.find((c) => Array.isArray(c));
        const record = arrayCandidate ? arrayCandidate[0] : data && typeof data === "object" && !Array.isArray(data) ? data : null;

        if (record && typeof record === "object") {
          // Store the mappingId from the record — required for update endpoint
          const recordMappingId = record.mappingId ?? record.id ?? record.mapping_id ?? null;
          setMappingId(recordMappingId);

          const rawShift = resolveShiftIdFromRecord(record);

          const effectiveFromStr = normalizeToIsoDate(record.effectiveFrom || record.startDate || record.fromDate);
          const effectiveToStr = normalizeToIsoDate(record.effectiveTo || record.endDate || record.toDate);

          if (rawShift || effectiveFromStr || effectiveToStr) {
            setForm({
              empId: String(initialEmployeeId),
              shiftId: rawShift,
              effectiveFrom: effectiveFromStr,
              effectiveTo: effectiveToStr,
              isActive: record.isActive !== false
            });
            setIsUpdate(true);
          }
        }
      }
    } catch (err) {
      console.error("Failed to load shift mapping info", err);
    }
  }, [initialEmployeeId, tenantCode, tenantId, sessionAccessToken]);

  useEffect(() => {
    if (!isActive) return;
    if (!initialEmployeeId) {
      setForm({
        empId: "",
        shiftId: "",
        effectiveFrom: "",
        effectiveTo: "",
        isActive: true
      });
      setIsUpdate(false);
      setMappingId(null);
      return;
    }
    setForm((prev) => ({ ...prev, empId: String(initialEmployeeId) }));
    fetchShiftMapping();
  }, [isActive, initialEmployeeId, fetchShiftMapping]);

  // If an existing mapping only returned a shift name, resolve it once options load.
  useEffect(() => {
    if (!form.shiftId || /^\d+$/.test(String(form.shiftId).trim()) || !shiftOptions.length) return;
    const match = shiftOptions.find(
      (option) =>
      option.value === form.shiftId ||
      option.label.toLowerCase() === String(form.shiftId).trim().toLowerCase()
    );
    if (match && match.value !== form.shiftId) {
      setForm((prev) => ({ ...prev, shiftId: match.value }));
    }
  }, [form.shiftId, shiftOptions]);

  const dropdownShiftOptions = useMemo(() => {
    if (!form.shiftId || !/^\d+$/.test(String(form.shiftId).trim())) return shiftOptions;
    if (shiftOptions.some((option) => option.value === String(form.shiftId))) return shiftOptions;
    return [...shiftOptions, { value: String(form.shiftId), label: `${i18n.t("onboarding.shift")} ${form.shiftId}` }];
  }, [form.shiftId, shiftOptions]);

  function validateForm() {
    const nextErrors = {};

    if (!String(form.empId || "").trim()) {
      nextErrors.empId = i18n.t("onboarding.errors.empIdRequired");
    } else if (!/^\d+$/.test(String(form.empId).trim())) {
      nextErrors.empId = i18n.t("onboarding.errors.empIdMustBeNumeric");
    }

    if (!String(form.shiftId || "").trim()) {
      nextErrors.shiftId = i18n.t("onboarding.errors.shiftRequired");
    } else if (!/^\d+$/.test(String(form.shiftId).trim())) {
      nextErrors.shiftId = i18n.t("onboarding.errors.chooseValidShift");
    }

    if (!form.effectiveFrom) {
      nextErrors.effectiveFrom = i18n.t("onboarding.errors.effectiveFromRequired");
    } else if (!isValidIsoDate(form.effectiveFrom)) {
      nextErrors.effectiveFrom = i18n.t("onboarding.errors.useValidDate");
    }

    if (!form.effectiveTo) {
      nextErrors.effectiveTo = i18n.t("onboarding.errors.effectiveToRequired");
    } else if (!isValidIsoDate(form.effectiveTo)) {
      nextErrors.effectiveTo = i18n.t("onboarding.errors.useValidDate");
    }

    if (
    isValidIsoDate(form.effectiveFrom) &&
    isValidIsoDate(form.effectiveTo) &&
    form.effectiveTo < form.effectiveFrom)
    {
      nextErrors.effectiveTo = i18n.t("onboarding.errors.effectiveToBeforeFrom");
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function handleSave() {
    setBanner(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (!shiftMappingAction.isAvailable) {
        throw new Error(buildOnboardingAccessMessage(i18n.t("onboarding.shiftMapping"), i18n.t("common.add")));
      }

      const payload = {
        empId: Number(form.empId.trim()),
        shiftId: Number(form.shiftId),
        effectiveFrom: form.effectiveFrom,
        effectiveTo: form.effectiveTo,
        isActive: Boolean(form.isActive)
      };

      // CORRECT UPDATE ENDPOINT: PUT /saas/hrms/emp-shift-mapping/update/{mappingId}
      // mappingId is the record ID returned by the search endpoint, NOT the empId
      let endpoint;
      let method;
      let serviceCode;

      if (isUpdate && mappingId != null) {
        endpoint = `/saas/hrms/emp-shift-mapping/update/${mappingId}`;
        method = "PUT";
        serviceCode = "HR25U";
      } else {
        endpoint = shiftMappingAction.endpoint || DEFAULT_SHIFT_MAPPING_ENDPOINT;
        method = shiftMappingAction.method || "POST";
        serviceCode = shiftMappingAction.serviceCode || DEFAULT_SHIFT_MAPPING_SERVICE_CODE;
      }

      const response = await fetch(resolveApiUrl(endpoint), {
        method: method,
        headers: {
          "Content-Type": "application/json",
          "x-tenant-code": tenantCode || sessionDB.getTenantCode() || "",
          "x-tenant-id": String(tenantId || sessionDB.getTenantId() || ""),
          "x-service-code": serviceCode,
          authorization: `Bearer ${sessionAccessToken}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const { errorMessage, errorBody } = await parseOnboardingErrorResponse(response);
        handleOnboardingApiError(errorBody, errorMessage, { setErrors, setBanner });
        return;
      }

      setBanner({ type: "success", msg: i18n.t("onboarding.messages.shiftMappingSavedSuccessfully") });
      await fetchShiftMapping();
      if (onSuccess) onSuccess();
    } catch (error) {
      handleOnboardingApiError(null, error.message, { setErrors, setBanner });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={`smp-page${isTabMode ? " is-tab-mode" : ""}`}>
      <main className="smp-main">
        {!isTabMode && breadcrumb &&
        <div className="smp-breadcrumb" aria-label={i18n.t("common.breadcrumb")}>
            {breadcrumb.map((item, index) =>
          <React.Fragment key={item.label}>
                {index > 0 && <span className="smp-bc-sep">/</span>}
                <button
              className={`smp-bc-item${item.active ? " active" : ""}`}
              onClick={item.active ? undefined : item.onClick}
              disabled={item.active}>

                  {item.label}
                </button>
              </React.Fragment>
          )}
          </div>
        }

        {!isTabMode &&
        <OnboardingPageTitle
          onBack={onBack}
          employeeId={initialEmployeeId}
          titleClassName="smp-page-title"
          subtitleClassName="smp-page-sub"
          title={<Trans i18nKey="onboarding.shiftMapping" />}
          subtitle={<Trans i18nKey="dateTime.manageShiftAssignmentAndEffectiveDatesForThe" />}
        />
        }

        <div className="smp-form-card">
          <div className="smp-section">
            <div className="smp-grid">
              <div className="smp-field">
                <label className="smp-label required" htmlFor="shiftId"><Trans i18nKey="onboarding.shift" /></label>
                <Select
                  value={form.shiftId ? String(form.shiftId) : ""}
                  onValueChange={(value) => {
                    setForm((prev) => ({ ...prev, shiftId: value }));
                  }}>

                  <SelectTrigger id="shiftId" className={`smp-select${errors.shiftId ? " error" : ""}`}>
                    <SelectValue placeholder={shiftsLoading ? i18n.t("onboarding.loadingShifts") : i18n.t("onboarding.selectShift")} />
                  </SelectTrigger>
                  <SelectContent>
                    {dropdownShiftOptions.map((option) =>
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <p className="smp-error">{errors.shiftId || ""}</p>
              </div>
              <div className="smp-field">
                <label className="smp-label required"><Trans i18nKey="dateTime.effectiveFrom" /></label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={`smp-input${errors.effectiveFrom ? " error" : ""} w-full h-auto justify-start text-left font-normal`}>

                      <CalendarIcon className="mr-2 h-4 w-4 per-file-hint" />
                      {form.effectiveFrom ? format(parse(form.effectiveFrom, "yyyy-MM-dd", new Date()), "PPP") : <span className="text-muted-foreground"><Trans i18nKey="dateTime.pickADate" /></span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
                    <Calendar
                      mode="single"
                      captionLayout="dropdown"
                      startMonth={new Date(1990, 0)}
                      endMonth={new Date(2100, 0)}
                      selected={isValidIsoDate(form.effectiveFrom) ? parse(form.effectiveFrom, "yyyy-MM-dd", new Date()) : undefined}
                      onSelect={(date) => {
                        if (!date) return;
                        const v = format(date, "yyyy-MM-dd");
                        setForm((prev) => ({ ...prev, effectiveFrom: v }));
                      }}
                      initialFocus
                      className="p-3 pointer-events-auto" />

                  </PopoverContent>
                </Popover>
                <p className="smp-error">{errors.effectiveFrom || ""}</p>
              </div>

              <div className="smp-field">
                <label className="smp-label required"><Trans i18nKey="dateTime.effectiveTo" /></label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={`smp-input${errors.effectiveTo ? " error" : ""} w-full h-auto justify-start text-left font-normal`}>

                      <CalendarIcon className="mr-2 h-4 w-4 per-file-hint" />
                      {form.effectiveTo ? format(parse(form.effectiveTo, "yyyy-MM-dd", new Date()), "PPP") : <span className="text-muted-foreground"><Trans i18nKey="dateTime.pickADate" /></span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
                    <Calendar
                      mode="single"
                      captionLayout="dropdown"
                      startMonth={new Date(1990, 0)}
                      endMonth={new Date(2100, 0)}
                      selected={isValidIsoDate(form.effectiveTo) ? parse(form.effectiveTo, "yyyy-MM-dd", new Date()) : undefined}
                      disabled={isValidIsoDate(form.effectiveFrom) ? { before: parse(form.effectiveFrom, "yyyy-MM-dd", new Date()) } : undefined}
                      onSelect={(date) => {
                        if (!date) return;
                        const v = format(date, "yyyy-MM-dd");
                        setForm((prev) => ({ ...prev, effectiveTo: v }));
                      }}
                      initialFocus
                      className="p-3 pointer-events-auto" />

                  </PopoverContent>
                </Popover>
                <p className="smp-error">{errors.effectiveTo || ""}</p>
              </div>


              <div className="smp-field span2">
                <label className="smp-label"><Trans i18nKey="status.active" /></label>
                <div className="smp-toggle-wrap">
                  <button type="button" className={`smp-toggle${form.isActive ? " active" : ""}`} onClick={() => setForm((prev) => ({ ...prev, isActive: !prev.isActive }))}>
                    <span className="smp-toggle-knob" />
                  </button>
                  <span className="smp-toggle-text">{form.isActive ? i18n.t("common.yes") : i18n.t("common.no")}</span>
                </div>
              </div>
            </div>
          </div>
          {(onBack || onNext || banner) &&
          <div className="smp-section">
              {banner &&
            <div className={`smp-banner ${banner.type}`} role="alert">
                  {banner.msg}
                </div>
            }
              {!isTabMode &&
            <div className="smp-actions">
                  <button className="smp-primary-btn" type="button" onClick={handleSave} disabled={loading || !shiftMappingAction.isAvailable}>
                    {loading ? i18n.t("localConveyance.saving") : i18n.t("formActions.save")}
                  </button>
                  {onNext ? <button className="smp-secondary-btn" type="button" onClick={onNext}><Trans i18nKey="pagination.next2" /></button> : null}
                </div>
            }
            </div>
          }
        </div>
      </main>
    </div>);

}
